import { useMemo, useState, type ReactNode } from "react";
import { Building, ChevronRight, Folder } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import type { OrgFolderOption } from "@/stores/organizations/repository";
import type { PickerProject } from "./subcontractorAssignments";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { useProjectStatusEnabled } from "@/domains/projects/status/projectStatusAvailability";
import { ProjectStatusFilter } from "@/domains/projects/status/ProjectStatusFilter";
import {
  orderActiveFirst,
  partitionProjectsByStatus,
  shouldSplitByStatus,
  type ProjectStatusFilterValue,
} from "@/domains/projects/status/projectStatus";

/** Folders resolved into a forest, keyed for the tri-state lookups below. */
export interface FolderIndex {
  byProject: Map<number, number[]>;
  /** The DRAWN tree: same-project and acyclic, so FolderTree terminates. */
  childrenByParent: Map<number, number[]>;
  /**
   * The SERVER's tree: every parent link that resolves to a real folder, a
   * cross-project or cyclic one included. folder_subtree_ids() walks parent_id
   * with no project predicate, so a folder the forest re-rooted is still a
   * descendant to the RPC — and the cascade has to read the same links the
   * expansion will, or a save denies a branch the picker drew ticked.
   */
  subtreeChildren: Map<number, number[]>;
  byId: Map<number, OrgFolderOption>;
}

function pushInto(map: Map<number, number[]>, key: number, folderId: number) {
  const list = map.get(key);
  if (list) list.push(folderId);
  else map.set(key, [folderId]);
}

/** True when walking up parentId revisits a folder — possible, parent_id has no FK. */
function hasCyclicAncestry(folder: OrgFolderOption, byId: Map<number, OrgFolderOption>): boolean {
  const seen = new Set<number>([folder.id]);
  let current = folder.parentId === null ? undefined : byId.get(folder.parentId);
  while (current) {
    if (seen.has(current.id)) return true;
    seen.add(current.id);
    current = current.parentId === null ? undefined : byId.get(current.parentId);
  }
  return false;
}

/**
 * Index the org's folders per project. `document_folders.parent_id` carries no
 * foreign key, so a parent can dangle, sit in another project, or form a cycle —
 * every folder we cannot hang off a real parent is rendered at the project root
 * rather than dropped. That leaves `childrenByParent` a forest, so the tree
 * renderer cannot recurse forever.
 *
 * Re-rooting is a RENDERING decision and stays one: `subtreeChildren` keeps the
 * link the forest refused, because the server keeps it too. A folder parented
 * across projects is still expanded as a descendant by folder_subtree_ids(), so
 * dropping the link from the cascade as well would let an admin untick the
 * parent, see the child stay ticked under its own project, and have the save
 * deny it anyway. It is drawn where it can be reached and denied where the
 * server will deny it.
 *
 * A folder that hangs off a DOCUMENT rather than a parent folder arrives with
 * that document's folder already resolved into `parentId`, so it indexes under
 * its real ancestor here — see OrgFolderOption. This tree and the server's
 * subtree walk then agree on where it sits, which is what stops such a folder
 * being drawn ticked at the project root while the server denies it with the
 * branch it actually belongs to. Where the document could not be read the
 * position is unknown, not root: the folder still indexes at the root because
 * that is the only place it can be reached from, and carries
 * `parentUnresolved` so the row can say the tick may not be the whole story.
 */
export function buildFolderIndex(folders: OrgFolderOption[]): FolderIndex {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const byProject = new Map<number, number[]>();
  const childrenByParent = new Map<number, number[]>();
  const subtreeChildren = new Map<number, number[]>();

  for (const folder of [...folders].sort((a, b) => a.name.localeCompare(b.name))) {
    const parent = folder.parentId === null ? undefined : byId.get(folder.parentId);
    if (parent) pushInto(subtreeChildren, parent.id, folder.id);
    if (parent && parent.projectId === folder.projectId && !hasCyclicAncestry(folder, byId)) {
      pushInto(childrenByParent, parent.id, folder.id);
    } else {
      pushInto(byProject, folder.projectId, folder.id);
    }
  }

  return { byProject, childrenByParent, subtreeChildren, byId };
}

/**
 * The folder plus its whole subtree, as folder_subtree_ids() expands it — every
 * resolvable parent link, not just the ones the forest drew. Visited-guarded, so
 * a cyclic index terminates.
 */
export function descendantIds(folderId: number, index: FolderIndex): number[] {
  const ids: number[] = [];
  const seen = new Set<number>();
  const stack: number[] = [folderId];

  while (stack.length > 0) {
    const id = stack.pop() as number;
    if (seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    stack.push(...(index.subtreeChildren.get(id) ?? []));
  }

  return ids;
}

/**
 * The folder's ancestors, nearest first — the mirror of `descendantIds`, so a
 * re-ticked folder releases exactly the folders whose expansion would reach it.
 * A parent id that resolves to no folder ends the walk, and a `seen` guard ends
 * a cyclic one; a parent in another project is still a parent here, because it
 * is one to the server.
 */
export function ancestorIds(folderId: number, index: FolderIndex): number[] {
  const ids: number[] = [];
  const seen = new Set<number>([folderId]);
  let folder = index.byId.get(folderId);

  while (folder) {
    const parentId = folder.parentId;
    if (parentId === null || seen.has(parentId)) break;
    const parent = index.byId.get(parentId);
    if (!parent) break;
    seen.add(parentId);
    ids.push(parentId);
    folder = parent;
  }

  return ids;
}

/**
 * The stored deny-list as the server will act on it. Rows are written
 * subtree-closed, but a folder created INSIDE an already-denied branch since
 * then has no row of its own: seeded raw it draws ticked, while Save re-sends
 * the branch and folder_subtree_ids() expands over it anyway — a save the admin
 * read as folder-neutral, silently removing access the picker showed as granted.
 * Closing the seed downwards makes the drawn state and the sent state one set.
 *
 * A HIDDEN or SOFT-DELETED seed folder closes over nothing, because the server
 * treats its row differently from every other: the reconcile DELETE in
 * org_set_subcontractor_project_scope is restricted to `is_deleted = false AND
 * hidden = false`, so that row survives whatever the payload says — and can also
 * never be lifted here. Expanding it would claim its visible descendants are
 * denied when a save has already released them (the reconcile deletes the
 * descendants and keeps the ancestor), and then re-deny them on the next save by
 * sending the branch back. Its own row needs no help staying; its descendants
 * are seeded from rows of their own, or they are not denied at all. Unticking a
 * visible ancestor still cascades onto it — that is toggleFolderExclusion, and
 * there the server does write the branch.
 *
 * An id matching no folder in the index rides through untouched: it is a denial
 * on something this picker cannot show, and dropping it would re-grant it.
 */
export function subtreeClosedExclusions(folderIds: number[], index: FolderIndex): Set<number> {
  const closed = new Set<number>();
  for (const folderId of folderIds) {
    const folder = index.byId.get(folderId);
    if (folder && (folder.isDeleted || folder.hidden)) continue;
    for (const id of descendantIds(folderId, index)) closed.add(id);
  }
  return closed;
}

/**
 * The same deny-list with every id an ancestor already covers dropped — what
 * actually has to travel to the server.
 *
 * Both save paths expand each folder they are sent over its own subtree
 * (folder_subtree_ids), so naming a folder whose parent is named too adds
 * nothing. Sending the closed set instead is not merely wasteful: the invite
 * function validates the ids with a single `.in("id", …)`, whose response
 * PostgREST caps at 1000 rows and whose filter travels in the query string — so
 * denying a branch with more descendants than that fails the whole invite with
 * "folders do not belong to the selected projects", and a wider one reaches a
 * 414 first. Reduced, the payload is bounded by the ticks the admin made.
 *
 * Greedy over the roots rather than "has an excluded ancestor": `parent_id`
 * carries no foreign key, so a cycle in which every member's parent is also
 * denied would drop the branch entirely and re-grant it. Every id is kept or is
 * a descendant of one that was, whatever the links look like.
 */
export function maximalExclusions(folderIds: number[], index: FolderIndex): number[] {
  const denied = new Set(folderIds);
  const covered = new Set<number>();
  const roots: number[] = [];

  // Shallowest first, so a parent is reached before the children it covers.
  const byDepth = [...folderIds].sort(
    (a, b) => ancestorIds(a, index).length - ancestorIds(b, index).length
  );

  for (const folderId of byDepth) {
    if (covered.has(folderId)) continue;
    roots.push(folderId);
    for (const id of descendantIds(folderId, index)) {
      if (denied.has(id)) covered.add(id);
    }
  }

  return roots;
}

/**
 * The folders to draw for `folderIds`, replacing each soft-deleted or hidden one
 * with its own renderable descendants rather than dropping the branch.
 *
 * A hidden ancestor does NOT imply hidden descendants here: `fetchFolders`
 * filters `hidden` / `is_deleted` flat with no ancestor cascade, so a visible
 * folder under a hidden parent is in the subcontractor's real folder tree.
 * Pruning at the hidden node would leave it unreachable in the picker — a
 * folder an admin cannot deny, which is exactly the hole in the grant this
 * feature exists to close.
 */
export function renderableFolderIds(
  folderIds: number[],
  index: FolderIndex,
  seen = new Set<number>()
): number[] {
  return folderIds.flatMap((id) => {
    if (seen.has(id)) return [];
    seen.add(id);
    const folder = index.byId.get(id);
    if (!folder) return [];
    if (!folder.isDeleted && !folder.hidden) return [id];
    return renderableFolderIds(index.childrenByParent.get(id) ?? [], index, seen);
  });
}

/**
 * Exclusions are a deny-list: no rows means full access, so a folder renders
 * ticked until it (or every folder under it) is excluded.
 */
export function folderCheckState(
  folderId: number,
  excluded: Set<number>,
  index: FolderIndex
): boolean | "indeterminate" {
  if (excluded.size === 0) return true;
  const ids = descendantIds(folderId, index);
  const excludedCount = ids.filter((id) => excluded.has(id)).length;
  if (excludedCount === 0) return true;
  if (excludedCount === ids.length) return false;
  return "indeterminate";
}

/**
 * Unticking excludes the whole subtree; ticking (or half-ticking) restores it,
 * *and every ancestor with it*.
 *
 * The ancestors are what keeps the set subtree-closed — a denied folder always
 * has its whole subtree denied — and that is not a nicety. Both save paths hand
 * this set to the server as a list of folders to deny, and the server expands
 * each one over its own subtree (folder_subtree_ids). Leaving a re-ticked folder
 * under a still-denied parent would therefore be a state the server cannot
 * store: it would re-deny the branch the admin deliberately kept, and the picker
 * would have shown a permission that never existed. Giving the ancestors back is
 * the honest reading of a tick anyway — the admin asked for this folder, and
 * nothing under a denied parent is reachable.
 */
export function toggleFolderExclusion(
  excluded: Set<number>,
  folderId: number,
  index: FolderIndex
): Set<number> {
  const next = new Set(excluded);
  if (folderCheckState(folderId, excluded, index) === true) {
    for (const id of descendantIds(folderId, index)) next.add(id);
    return next;
  }
  for (const id of descendantIds(folderId, index)) next.delete(id);
  for (const id of ancestorIds(folderId, index)) next.delete(id);
  return next;
}

/**
 * The folders whose branch starts open: every one that is partly denied — some
 * of its subtree excluded, not all of it. A narrowed grant then shows where it
 * was narrowed the moment its project is expanded, instead of hiding behind a
 * half-ticked row the admin has to dig through. Exclusions are subtree-closed,
 * so a non-excluded ancestor of an excluded folder is exactly a partly denied one.
 */
function partiallyExcludedFolderIds(excluded: Set<number>, index: FolderIndex): Set<number> {
  const open = new Set<number>();
  for (const folderId of excluded) {
    for (const id of ancestorIds(folderId, index)) {
      if (!excluded.has(id)) open.add(id);
    }
  }
  return open;
}

function toggleInSet(set: Set<number>, id: number): Set<number> {
  const next = new Set(set);
  if (!next.delete(id)) next.add(id);
  return next;
}

interface ExpandToggleProps {
  expanded: boolean;
  /** The row's name, for the button's accessible label. */
  name: string;
  onToggle: () => void;
}

/**
 * The chevron that opens or closes a row's children. Expanding only changes
 * what is drawn, never what is granted, so it stays usable while a save runs.
 */
function ExpandToggle({ expanded, name, onToggle }: ExpandToggleProps) {
  const { t } = useLanguage();
  const label = expanded ? t.settings.roles.collapseItem : t.settings.roles.expandItem;
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={label.replace("{name}", name)}
      onClick={onToggle}
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ChevronRight
        className={cn("h-4 w-4 transition-transform", expanded && "rotate-90")}
        aria-hidden
      />
    </button>
  );
}

/** Holds the toggle's place on a row with nothing to expand, so the ticks line up. */
function ExpandToggleSpacer() {
  return <span className="h-5 w-5 shrink-0" aria-hidden />;
}

interface FolderTreeProps {
  folderIds: number[];
  index: FolderIndex;
  excludedFolderIds: Set<number>;
  onToggleFolder: (folderId: number) => void;
  expandedFolderIds: Set<number>;
  onToggleExpanded: (folderId: number) => void;
  disabled?: boolean;
}

function FolderTree({
  folderIds,
  index,
  excludedFolderIds,
  onToggleFolder,
  expandedFolderIds,
  onToggleExpanded,
  disabled,
}: FolderTreeProps) {
  const { t } = useLanguage();
  return (
    <div className={"ml-2.5 space-y-1 border-l pl-3.5"}>
      {folderIds.map((folderId) => {
        const folder = index.byId.get(folderId);
        if (!folder) return null;
        // Soft-deleted and hidden folders are not shown, but they stay in the
        // index so unticking an ancestor still excludes them — a document
        // restore un-deletes every ancestor folder, and a subtree with no
        // exclusion rows would come back visible. Their own visible children are
        // hoisted here instead of vanishing with them.
        const state = folderCheckState(folderId, excludedFolderIds, index);
        const children = renderableFolderIds(index.childrenByParent.get(folderId) ?? [], index);
        const expanded = children.length > 0 && expandedFolderIds.has(folderId);
        return (
          <div key={folderId} className="space-y-1">
            <div className="flex min-w-0 items-center gap-1">
              {children.length > 0 ? (
                <ExpandToggle
                  expanded={expanded}
                  name={folder.name}
                  onToggle={() => onToggleExpanded(folderId)}
                />
              ) : (
                <ExpandToggleSpacer />
              )}
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={state === true}
                  indeterminate={state === "indeterminate"}
                  onCheckedChange={() => onToggleFolder(folderId)}
                  disabled={disabled}
                />
                <Folder className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 truncate">{folder.name}</span>
                {/* Drawn here because its document could not be read, not because
                    it belongs here. The server can still deny it with the branch
                    it truly hangs under, so the tick above is not the whole truth
                    about this folder — say so rather than let the position pass
                    for fact. See OrgFolderOption.parentUnresolved. */}
                {folder.parentUnresolved && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t.settings.roles.folderPositionUnknown}
                  </span>
                )}
              </label>
            </div>
            {expanded && (
              <FolderTree
                folderIds={children}
                index={index}
                excludedFolderIds={excludedFolderIds}
                onToggleFolder={onToggleFolder}
                expandedFolderIds={expandedFolderIds}
                onToggleExpanded={onToggleExpanded}
                disabled={disabled}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * What is ticked, and what a tick does. The four travel together because they
 * are one decision — a project's folders are only reachable once the project
 * itself is ticked, and both toggles write the same admin's edit — and each
 * caller holds them in one place: the manage dialog's two useState pairs, the
 * invite dialog's two setters.
 */
export interface ProjectFolderSelection {
  selectedProjectIds: Set<number>;
  excludedFolderIds: Set<number>;
  onToggleProject: (projectId: number) => void;
  onToggleFolder: (folderId: number) => void;
}

export interface ProjectListProps {
  projects: PickerProject[];
  selectedProjectIds: Set<number>;
  onToggleProject: (projectId: number) => void;
  /** The project whose settings the other pane shows. */
  activeProjectId: number | null;
  onActivateProject: (projectId: number) => void;
  /** A short summary beside a ticked project, e.g. its access level. */
  projectBadge?: (projectId: number) => ReactNode;
  /**
   * Draw a ticked project's folders under its row while it is expanded, so the
   * admin narrows a project's folders where they chose it. Opening or ticking a
   * project expands it; the chevrons fold any project or folder away again.
   * Omitted, the list offers projects only.
   */
  folders?: {
    index: FolderIndex;
    excludedFolderIds: Set<number>;
    onToggleFolder: (folderId: number) => void;
  };
  /** Copy for "this org has nothing to offer" — the two callers word it differently. */
  emptyMessage: string;
  disabled?: boolean;
  /** Height of the scrolling list; the dialogs size it to the viewport. */
  className?: string;
}

/**
 * The project list shared by the invite and manage-member dialogs: projects
 * grouped by building, a tick per project for access, and the row itself to
 * choose which project the settings pane shows.
 */
function ProjectBuildingHeading({ buildingName }: { buildingName: string }) {
  return (
    <p
      data-building-heading
      className="flex min-w-0 items-center gap-1.5 px-2 text-xs font-semibold uppercase text-muted-foreground"
    >
      <Building className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 truncate">{buildingName}</span>
    </p>
  );
}

function ProjectUnavailableLabel({ unavailable }: { unavailable?: boolean }) {
  const { t } = useLanguage();
  return unavailable ? (
    <span className="shrink-0 text-xs text-muted-foreground">
      {t.settings.roles.projectUnavailable}
    </span>
  ) : null;
}

function projectFolderIds(
  projectId: number,
  selected: boolean,
  folders: ProjectListProps["folders"]
) {
  if (!selected || !folders) return [];
  return renderableFolderIds(folders.index.byProject.get(projectId) ?? [], folders.index);
}

export function ProjectList({
  projects,
  selectedProjectIds,
  onToggleProject,
  activeProjectId,
  onActivateProject,
  projectBadge,
  folders,
  emptyMessage,
  disabled,
  className,
}: ProjectListProps) {
  const { t } = useLanguage();
  const [toggledProjectIds, setToggledProjectIds] = useState<Set<number>>(() => new Set());

  // What is drawn open — view state only, never part of the grant. The project
  // the admin starts on opens with it, and partly denied branches start open so
  // a narrowed grant shows where it was narrowed (see partiallyExcludedFolderIds).
  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<number>>(
    () => new Set(activeProjectId === null ? [] : [activeProjectId])
  );
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(() =>
    folders ? partiallyExcludedFolderIds(folders.excludedFolderIds, folders.index) : new Set()
  );

  const expandProject = (projectId: number) => {
    setExpandedProjectIds((current) => {
      if (current.has(projectId)) return current;
      const next = new Set(current);
      next.add(projectId);
      return next;
    });
  };
  const toggleProjectExpanded = (projectId: number) =>
    setExpandedProjectIds((current) => toggleInSet(current, projectId));
  const toggleFolderExpanded = (folderId: number) =>
    setExpandedFolderIds((current) => toggleInSet(current, folderId));

  // Opening a project is asking to work in it, so its folders unfold with it.
  const activateProject = (projectId: number) => {
    expandProject(projectId);
    onActivateProject(projectId);
  };

  // Status filter. A ticked, just-toggled or open project always stays listed
  // whatever the filter, so narrowing the view can never hide — and thereby
  // silently keep or drop — a grant the admin is looking at.
  const [statusFilter, setStatusFilter] = useState<ProjectStatusFilterValue>("active");
  const statusEnabled = useProjectStatusEnabled();
  const statusCounts = useMemo(() => {
    const { active, inactive } = partitionProjectsByStatus(projects);
    return { active: active.length, inactive: inactive.length, all: projects.length };
  }, [projects]);
  const showStatusFilter = shouldSplitByStatus(projects.length, statusEnabled);
  const shownProjects = useMemo(
    () =>
      !showStatusFilter || statusFilter === "all"
        ? projects
        : projects.filter(
            (p) =>
              p.status === statusFilter ||
              selectedProjectIds.has(p.id) ||
              toggledProjectIds.has(p.id) ||
              p.id === activeProjectId
          ),
    [
      projects,
      showStatusFilter,
      statusFilter,
      selectedProjectIds,
      toggledProjectIds,
      activeProjectId,
    ]
  );

  const toggleProject = (projectId: number) => {
    // A fresh tick grants every folder; unfold them so the admin sees what that
    // covers and can narrow it in place.
    if (!selectedProjectIds.has(projectId)) expandProject(projectId);
    setToggledProjectIds((current) => {
      if (current.has(projectId)) return current;
      const next = new Set(current);
      next.add(projectId);
      return next;
    });
    onToggleProject(projectId);
  };

  const projectsByBuilding = useMemo(() => {
    const groups = new Map<string, PickerProject[]>();
    for (const p of shownProjects) {
      const list = groups.get(p.buildingName) ?? [];
      list.push(p);
      groups.set(p.buildingName, list);
    }
    return Array.from(
      groups,
      ([buildingName, buildingProjects]) =>
        [buildingName, orderActiveFirst(buildingProjects)] as const
    );
  }, [shownProjects]);

  if (projects.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <>
      {showStatusFilter && (
        <ProjectStatusFilter
          variant="inline"
          value={statusFilter}
          onValueChange={setStatusFilter}
          counts={statusCounts}
        />
      )}
      <ScrollArea className={cn("h-80 rounded-md border md:h-[26rem]", className)}>
        <div className="space-y-4 p-2">
          {projectsByBuilding.map(([buildingName, buildingProjects]) => (
            <div key={buildingName} className="space-y-1">
              <ProjectBuildingHeading buildingName={buildingName} />
              {buildingProjects.map((project) => {
                const selected = selectedProjectIds.has(project.id);
                const active = project.id === activeProjectId;
                // Folders are only offered once the project is ticked: unticked,
                // the member has no access to narrow. A project with no folders
                // has nothing to expand.
                const folderIds = projectFolderIds(project.id, selected, folders);
                const expandable = folderIds.length > 0;
                const expanded = expandable && expandedProjectIds.has(project.id);
                return (
                  <div key={project.id} className="space-y-1">
                    <div
                      data-project-row
                      className={cn(
                        "flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm transition-colors",
                        active ? "border-primary" : "border-transparent hover:border-primary/40"
                      )}
                    >
                      {folders &&
                        (expandable ? (
                          <ExpandToggle
                            expanded={expanded}
                            name={project.name}
                            onToggle={() => toggleProjectExpanded(project.id)}
                          />
                        ) : (
                          <ExpandToggleSpacer />
                        ))}
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleProject(project.id)}
                        disabled={disabled}
                        aria-label={project.name}
                      />
                      <ProjectStatusBadge variant="dot" status={project.status} />
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        aria-current={active ? "true" : undefined}
                        onClick={() => activateProject(project.id)}
                      >
                        <span className="min-w-0 truncate">{project.name}</span>
                        <ProjectUnavailableLabel unavailable={project.unavailable} />
                        <span className="ml-auto flex shrink-0 items-center gap-1">
                          {selected && projectBadge?.(project.id)}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                        </span>
                      </button>
                    </div>
                    {folders && expanded && (
                      <div data-project-folders={project.id} className="pb-1 pl-2">
                        <FolderTree
                          folderIds={folderIds}
                          index={folders.index}
                          excludedFolderIds={folders.excludedFolderIds}
                          onToggleFolder={folders.onToggleFolder}
                          expandedFolderIds={expandedFolderIds}
                          onToggleExpanded={toggleFolderExpanded}
                          disabled={disabled}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </ScrollArea>
    </>
  );
}
